import { useCallback, useEffect, useState } from "react";
import { Loader2, Search, User, Users } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Card } from "./_shared";

// ---------------------------------------------------------------------------
// Audit Tab — read-only history of every admin write
// ---------------------------------------------------------------------------
export const AUDIT_ACTIONS = [
  { key: "",       label: "All" },
  { key: "create", label: "Create", color: "#10B981" },
  { key: "update", label: "Update", color: "#A78BFA" },
  { key: "delete", label: "Delete", color: "#EF4444" },
  { key: "import", label: "Import", color: "#6A9CFF" },
  { key: "status", label: "Status", color: "#F59E0B" },
  { key: "grant",  label: "Grant",  color: "#FF8A5C" },
  { key: "revoke", label: "Revoke", color: "#F472B6" },
  { key: "ban",    label: "Ban",    color: "#DC2626" },
];
export const AUDIT_ENTITIES = [
  { key: "",         label: "All entities" },
  { key: "merchant", label: "Merchants" },
  { key: "program",  label: "Programs" },
  { key: "report",   label: "Reports" },
];

export function AuditTab() {
  const { apiFetch } = useAuth();
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [actors, setActors] = useState([]);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const limit = 30;

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = new URLSearchParams({
        q, action, entity, actor: actorFilter,
        limit: String(limit), offset: String(offset),
      });
      const d = await apiFetch(`/api/audit?${params}`);
      setItems(d.items || []);
      setTotal(d.total || 0);
      setActors(d.actors || []);
    } catch { setItems([]); setTotal(0); }
  }, [apiFetch, q, action, entity, actorFilter, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [q, action, entity, actorFilter]);

  const actionColor = (a) => AUDIT_ACTIONS.find((x) => x.key === a)?.color || "#A1A1AA";

  return (
    <div className="space-y-px">
      <Card title="AUDIT LOG"
            subtitle="Every admin write — create, update, delete, import, status change — recorded with actor name and field diff."
            accent="#F472B6" testid="audit-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--srs-ink-faint)] pointer-events-none" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="search summary or entity_id…"
              data-testid="audit-search-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#F472B6] outline-none pl-10 pr-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] font-mono transition-colors"
            />
          </div>
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--srs-ink-faint)] pointer-events-none" />
            <input
              value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
              placeholder="filter by actor…"
              data-testid="audit-actor-input"
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] focus:border-[#F472B6] outline-none pl-10 pr-4 py-2.5 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors"
              list="audit-actor-list"
            />
            <datalist id="audit-actor-list">
              {actors.map((a) => <option key={a.actor} value={a.actor} />)}
            </datalist>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="audit-action-filter">
          {AUDIT_ACTIONS.map((a) => (
            <button key={a.key || "all"} onClick={() => setAction(a.key)}
              data-testid={`audit-action-${a.key || "all"}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border transition-colors ${action === a.key ? "text-black bg-[#F472B6] border-[#F472B6]" : "text-[var(--srs-ink-dim)] border-[var(--srs-line)] hover:text-[var(--srs-ink)]"}`}>
              {a.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="audit-entity-filter">
          {AUDIT_ENTITIES.map((e) => (
            <button key={e.key || "all"} onClick={() => setEntity(e.key)}
              data-testid={`audit-entity-${e.key || "all"}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border transition-colors ${entity === e.key ? "text-[var(--srs-ink)] border-[#A1A1AA] bg-[var(--srs-line-soft)]" : "text-[var(--srs-ink-dim)] border-[var(--srs-line)] hover:text-[var(--srs-ink)]"}`}>
              {e.label}
            </button>
          ))}
          <span className="ml-auto text-[10px] tracking-[0.2em] text-[var(--srs-ink-faint)] font-bold" data-testid="audit-total">
            {total.toLocaleString()} entr{total === 1 ? "y" : "ies"}
          </span>
        </div>

        {items === null && (
          <div className="text-xs text-[var(--srs-ink-faint)] flex items-center gap-2 py-8 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}
        {items?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="audit-empty">
            No audit entries match.
          </div>
        )}

        <div className="flex flex-col" data-testid="audit-list">
          {items?.map((it) => {
            const open = expanded === it.id;
            return (
              <div key={it.id} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] -mt-px first:mt-0" data-testid={`audit-row-${it.id}`}>
                <button
                  onClick={() => setExpanded(open ? null : it.id)}
                  className="w-full px-4 py-3 flex items-start gap-3 hover:bg-[var(--srs-surface)] transition-colors text-left">
                  <div className="flex flex-col items-center gap-1 shrink-0 w-[68px] pt-0.5">
                    <span
                      className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                      style={{ background: actionColor(it.action), color: ["update", "import", "delete", "ban"].includes(it.action) ? "#fff" : "#000" }}>
                      {it.action}
                    </span>
                    <span className="text-[9px] text-[var(--srs-ink-faint)] tracking-wide uppercase">{it.entity}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--srs-ink)] leading-snug break-words">{it.summary || "—"}</div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] text-[var(--srs-ink-faint)] tracking-wide">
                      <span className="text-[#A78BFA]" data-testid={`audit-actor-${it.id}`}>{it.actor}</span>
                      <span>{new Date(it.at).toLocaleString()}</span>
                      {it.entity_id && <span className="font-mono truncate max-w-[40%]">{it.entity_id}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] text-[var(--srs-ink-faint)] shrink-0 pt-1">{open ? "▴" : "▾"}</span>
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1 border-t border-[var(--srs-line-soft)] text-[11px] leading-relaxed text-[var(--srs-ink-dim)] font-mono space-y-3">
                    {it.before && (
                      <div>
                        <div className="text-[9px] tracking-[0.25em] font-bold text-[#EF4444] mb-1">BEFORE</div>
                        <pre className="whitespace-pre-wrap break-words bg-[var(--srs-bg)] border border-[var(--srs-line-soft)] p-3">{JSON.stringify(it.before, null, 2)}</pre>
                      </div>
                    )}
                    {it.after && (
                      <div>
                        <div className="text-[9px] tracking-[0.25em] font-bold text-[#10B981] mb-1">AFTER</div>
                        <pre className="whitespace-pre-wrap break-words bg-[var(--srs-bg)] border border-[var(--srs-line-soft)] p-3">{JSON.stringify(it.after, null, 2)}</pre>
                      </div>
                    )}
                    {it.details && (
                      <div>
                        <div className="text-[9px] tracking-[0.25em] font-bold text-[var(--srs-ink-dim)] mb-1">DETAILS</div>
                        <pre className="whitespace-pre-wrap break-words bg-[var(--srs-bg)] border border-[var(--srs-line-soft)] p-3">{JSON.stringify(it.details, null, 2)}</pre>
                      </div>
                    )}
                    {!it.before && !it.after && !it.details && (
                      <div className="text-[var(--srs-ink-faint)] italic">No diff data.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {total > limit && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-[var(--srs-line-soft)]" data-testid="audit-pagination">
            <button onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={offset === 0}
              data-testid="audit-prev"
              className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#F472B6] hover:text-[var(--srs-ink)] text-[var(--srs-ink-dim)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              ← Prev
            </button>
            <span className="text-[10px] tracking-[0.2em] text-[var(--srs-ink-faint)] font-bold">
              {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
            </span>
            <button onClick={() => setOffset((o) => o + limit)} disabled={offset + limit >= total}
              data-testid="audit-next"
              className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#F472B6] hover:text-[var(--srs-ink)] text-[var(--srs-ink-dim)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              Next →
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Users Tab (owner-only) — list / invite / reset password / remove admins
// ---------------------------------------------------------------------------
